import {
  LiveVoiceSessionCore,
  type LiveVoiceCoreOptions,
  type LiveVoiceTransportEvents,
} from './live-voice-session-core';
import { waitForLiveVoiceIce } from './live-voice-ice';
import {
  observeLiveVoiceInputActivity,
  observeLiveVoicePlayback,
  type LiveVoicePlayback,
} from './live-voice-playback';

export type { LiveVoiceFunctionCall } from './live-voice-function-calls';
export type LiveVoiceSessionOptions = Pick<
  LiveVoiceCoreOptions,
  | 'isCurrentCall'
  | 'onEvent'
  | 'onFailure'
  | 'executeTool'
  | 'onTranscriptFlush'
> & {
  micStream: MediaStream;
  audioElement: HTMLAudioElement;
  onRemoteStream: (stream: MediaStream) => void;
  onPlaybackChange?: (speaking: boolean) => void;
  onInputActivity?: (active: boolean) => void;
};

/** Browser media adapter; protocol, tool dedupe and final drain are shared. */
export class LiveVoiceSession extends LiveVoiceSessionCore {
  constructor(options: LiveVoiceSessionOptions) {
    let playback: LiveVoicePlayback | null = null;
    let input: { dispose: () => void } | null = null;
    let remote: MediaStream | null = null;
    let stopped = false;
    const createTransport = (events: LiveVoiceTransportEvents) => {
      const peer = new RTCPeerConnection();
      if (options.onInputActivity)
        input = observeLiveVoiceInputActivity({
          stream: options.micStream,
          onInputActivity: options.onInputActivity,
        });
      peer.addEventListener('track', (event) => {
        if (stopped || !options.isCurrentCall()) return;
        remote = event.streams[0] ?? new MediaStream([event.track]);
        options.audioElement.srcObject = remote;
        playback?.dispose();
        playback = observeLiveVoicePlayback({
          stream: remote,
          audioElement: options.audioElement,
          onPlaybackChange: options.onPlaybackChange,
        });
        options.onRemoteStream(remote);
        void options.audioElement.play().catch(() => {
          if (!stopped && options.isCurrentCall())
            options.onFailure?.('playback');
        });
      });
      peer.addEventListener('connectionstatechange', () =>
        events.connectionChanged(peer.connectionState)
      );
      for (const track of options.micStream.getAudioTracks())
        peer.addTrack(track, options.micStream);
      const channel = peer.createDataChannel('oai-events');
      channel.addEventListener('message', (event) =>
        events.message(event.data)
      );
      channel.addEventListener('error', events.failure);
      channel.addEventListener('close', events.failure);
      return {
        channel,
        async offer(signal: AbortSignal) {
          const offer = await peer.createOffer();
          if (signal.aborted) throw new Error('Live voice start cancelled');
          await peer.setLocalDescription(offer);
          await waitForLiveVoiceIce({
            complete: () => peer.iceGatheringState === 'complete',
            signal,
            listen: (check) => {
              peer.addEventListener('icegatheringstatechange', check);
              return () =>
                peer.removeEventListener('icegatheringstatechange', check);
            },
          });
          return peer.localDescription?.sdp ?? '';
        },
        accept: (sdp: string) =>
          peer.setRemoteDescription({ type: 'answer', sdp }),
        close: () => peer.close(),
      };
    };
    super({
      ...options,
      createTransport,
      muteMedia: (muted) => {
        for (const track of options.micStream.getAudioTracks())
          track.enabled = !muted;
      },
      interruptPlayback: () => playback?.interrupt(),
      stopMedia: () => {
        stopped = true;
        const cleanup = [
          () => playback?.dispose(),
          () => input?.dispose(),
          ...options.micStream.getTracks().map((track) => () => track.stop()),
          ...(remote?.getTracks() ?? []).map((track) => () => track.stop()),
          () => options.audioElement.pause(),
          () => {
            options.audioElement.srcObject = null;
          },
        ];
        for (const release of cleanup) {
          try {
            release();
          } catch {
            /* Release the remaining resources. */
          }
        }
        playback = null;
        input = null;
      },
    });
  }
}
